import { useId } from 'react';
import { isImageGraphic, type SymbolGraphic } from '../../../symbols/model';
import {
  backingGeometry,
  changeBackingShape,
  minimumBackingSize,
  type BackingShape,
  type SymbolBacking,
} from '../../../symbols/backing';
import { monochromeSvg } from '../../../symbols/tint';
import { useAppStore } from '../../state/useAppStore';
import { ColourControl } from './ColourControl';
import { NumericControl } from './NumericControl';
import { ContextHelp } from '../../../components/ContextHelp';
import { useSymbolArtworkMetrics } from './symbolResourcesContext';

export function BackingControls({
  graphic,
  colour,
  tint,
  size,
  backing,
  onChange,
}: {
  readonly graphic: SymbolGraphic;
  readonly colour: string;
  readonly tint?: string | null | undefined;
  readonly size: number;
  readonly backing?: SymbolBacking | undefined;
  readonly onChange: (backing: SymbolBacking) => void;
}) {
  const id = useId();
  const assets = useAppStore((s) => s.project.symbolAssets);
  const prepared = useSymbolArtworkMetrics(graphic);
  if (!isImageGraphic(graphic)) return null;
  const asset = graphic.kind === 'asset' ? assets.find((a) => a.id === graphic.assetId) : undefined;
  const initialColour =
    graphic.kind === 'builtin' ? colour : asset && tint && monochromeSvg(asset) ? tint : undefined;
  const aspect = prepared ?? asset ?? { width: 15, height: 15 };
  const update = (patch: Partial<SymbolBacking>) => {
    if (backing) onChange({ ...backing, ...patch });
  };
  const legacyTriangle = backing?.shape === 'triangle';
  const minimum = backing ? backingGeometry({ ...backing, scale: 1 }, aspect, size) : null;
  const current = backing ? backingGeometry(backing, aspect, size) : null;
  const minimumSize = backing
    ? legacyTriangle
      ? Math.max(minimum!.width, minimum!.height)
      : minimumBackingSize(backing, aspect, size)
    : 0;
  const currentSize =
    current && backing
      ? Math.max(current.shapeWidth, current.shapeHeight) + backing.borderWidth
      : 0;
  const choose = (shape: BackingShape | 'off') => {
    if (shape !== 'off' || backing)
      onChange(
        changeBackingShape(backing, shape, initialColour, {
          artwork: aspect,
          foregroundSize: size,
        }),
      );
  };
  return (
    <div className="symbol-backing-controls">
      <div className="symbol-backing-heading">
        <span>Background shape</span>
        <ContextHelp
          label="Symbol background"
          text="Place a circle, square or rounded square behind the icon. It is off by default. Fill, border and size are independent of the icon; turning it off keeps your settings."
        />
      </div>
      <div className="symbol-backing-shapes" role="group" aria-label="Background shape">
        {(['off', 'circle', 'square', 'rounded-square'] as const).map((shape) => (
          <button
            type="button"
            key={shape}
            aria-label={
              shape === 'off'
                ? 'No background'
                : shape === 'rounded-square'
                  ? 'Rounded square background'
                  : `${shape[0]!.toUpperCase()}${shape.slice(1)} background`
            }
            aria-pressed={
              shape === 'off' ? !backing?.enabled : backing?.enabled && backing.shape === shape
            }
            disabled={!prepared && shape !== 'off'}
            onClick={() => choose(shape)}
          >
            <svg
              viewBox="0 0 20 20"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.25"
            >
              {shape === 'off' ? (
                <path d="M4 16 16 4" />
              ) : shape === 'circle' ? (
                <circle cx="10" cy="10" r="7" />
              ) : shape === 'square' ? (
                <rect x="3" y="3" width="14" height="14" />
              ) : (
                <rect x="3" y="3" width="14" height="14" rx="2.1" />
              )}
            </svg>
            <span>
              {shape === 'off'
                ? 'Off'
                : shape === 'rounded-square'
                  ? 'Rounded square'
                  : shape[0]!.toUpperCase() + shape.slice(1)}
            </span>
          </button>
        ))}
      </div>
      {legacyTriangle ? (
        <button
          type="button"
          className="symbol-backing-legacy"
          aria-label="Triangle background (legacy)"
          aria-pressed={backing.enabled}
          disabled={!prepared}
          onClick={() => choose('triangle')}
        >
          Triangle (legacy)
        </button>
      ) : null}
      {backing?.enabled && current ? (
        <details className="symbol-backing-settings" open>
          <summary>Fill, border and size</summary>
          <div className="colour-field-pair">
            <ColourControl
              id={`${id}-fill`}
              label="Background fill"
              value={backing.fillColor}
              onChange={(fillColor) => update({ fillColor })}
            />
            <ColourControl
              id={`${id}-border`}
              label="Background border"
              value={backing.borderColor}
              onChange={(borderColor) => update({ borderColor })}
            />
          </div>
          <NumericControl
            id={`${id}-fill-alpha`}
            label="Background fill opacity"
            min={0}
            max={100}
            step={1}
            suffix="%"
            value={Math.round(backing.fillOpacity * 100)}
            onChange={(n) => update({ fillOpacity: Math.max(0, Math.min(100, n)) / 100 })}
          />
          <NumericControl
            id={`${id}-border-alpha`}
            label="Background border opacity"
            min={0}
            max={100}
            step={1}
            suffix="%"
            value={Math.round(backing.borderOpacity * 100)}
            onChange={(n) => update({ borderOpacity: Math.max(0, Math.min(100, n)) / 100 })}
          />
          <NumericControl
            id={`${id}-border-width`}
            label="Background border width"
            min={0}
            max={8}
            step={0.25}
            suffix="px"
            value={backing.borderWidth}
            onChange={(n) => update({ borderWidth: Math.max(0, Math.min(8, n)) })}
          />
          <NumericControl
            id={`${id}-size`}
            label="Background size"
            min={Number(minimumSize.toFixed(1))}
            max={Number(
              (legacyTriangle
                ? (minimumSize - backing.borderWidth) * 2.5 + backing.borderWidth
                : Math.min(2048, Math.max(minimumSize * 2.5, currentSize))
              ).toFixed(1),
            )}
            step={0.5}
            suffix="px"
            value={Number(currentSize.toFixed(1))}
            onChange={(n) =>
              update(
                legacyTriangle
                  ? {
                      scale: Math.max(
                        1,
                        Math.min(
                          2.5,
                          (n - backing.borderWidth) / (minimumSize - backing.borderWidth),
                        ),
                      ),
                    }
                  : { size: Math.max(0, Math.min(2048, n)) },
              )
            }
          />
        </details>
      ) : null}
    </div>
  );
}
